import React, { useEffect, useState, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import ContactSection from '../components/ContactSection';
import ServiceVisualization from '../components/ServiceVisualization';
import { servicesData, techIcons } from '../data/services';
import '../styles/service-detail.css';
import '../styles/hero-buttons.css';

const ServiceDetailPage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const [show3D, setShow3D] = useState(false);
  
  const service = serviceId ? servicesData[serviceId] : null;

  useEffect(() => {
    if (!service && serviceId) {
      navigate('/404');
    }
  }, [service, serviceId, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow3D(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  if (!service) {
    return null;
  }

  const heroStyles = {
    section: {
      position: 'relative' as const,
      width: '100%',
      margin: 0,
      backgroundColor: '#000000',
      overflow: 'hidden' as const,
      padding: '0 0 4rem 0',
      marginBottom: '2rem'
    },
    backgroundPattern: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center top',
      backgroundSize: '120% auto',
      opacity: 0.2,
      zIndex: 1,
      pointerEvents: 'none' as const
    },
    noiseOverlay: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
      opacity: 0.12,
      mixBlendMode: 'overlay' as const,
      zIndex: 2,
      pointerEvents: 'none' as const
    }
  };

  return (
    <Layout 
      title={`${service.title.replace(/\n/g, ' ')} | StimmDigital`}
      description={service.description}
    >
      <style>{`
        .hero-content-wrapper {
          display: grid;
          grid-template-columns: 1fr 500px;
          gap: 2rem;
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
          align-items: center;
          padding: 0 2rem;
        }
        
        .hero-text-content {
          position: relative;
          z-index: 20;
          width: 100%;
        }
        
        .hero-sphere-content {
          position: relative;
          z-index: 10;
          width: 100%;
        }
        
        @media (max-width: 1024px) {
          .hero-content-wrapper {
            grid-template-columns: 1fr;
            place-items: center;
            text-align: center;
          }
          
          .hero-text-content {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          
          .hero-buttons {
            justify-content: center !important;
          }
          
          .hero-sphere-content {
            max-width: 90vw;
            display: flex;
            justify-content: center;
          }
        }

        @media (max-width: 640px) {
          .hero-buttons {
            flex-direction: column !important;
            width: 100% !important;
            align-items: center !important;
          }
        }
      `}</style>

      <div className="service-detail-page">
        
        {/* Hero Section */}
        <div style={heroStyles.section}>
          <div style={heroStyles.backgroundPattern} />
          <div style={heroStyles.noiseOverlay} />

          <div className="hero-content-wrapper">
            {/* Text Content */}
            <div className="hero-text-content">
              <div className="service-badge" style={{ marginBottom: '1.5rem' }}>Service</div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                style={{ 
                  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                  fontWeight: 'bold',
                  color: 'white',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  marginBottom: '1.5rem',
                  whiteSpace: 'pre-line'
                }}
              >
                {service.title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                style={{ 
                  color: '#9ca3af',
                  fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                  lineHeight: 1.6,
                  marginBottom: '2rem',
                  maxWidth: '40rem'
                }}
              >
                {service.description}
              </motion.p>

              <motion.div
                className="hero-buttons"
                style={{ 
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '1rem',
                  width: '100%',
                  justifyContent: 'flex-start',
                  flexWrap: 'wrap',
                  maxWidth: '600px'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <button 
                  className="hero-btn-primary pulse"
                  data-action="contact"
                  onClick={() => {
                    const event = new CustomEvent('contactButtonClick');
                    document.dispatchEvent(event);
                  }}
                >
                  <span className="btn-content">
                    <i className="fas fa-calendar btn-icon"></i>
                    Kostenloses Beratungsgespräch
                  </span>
                </button>
                <button 
                  className="hero-btn-secondary"
                  onClick={() => {
                    document.getElementById('details')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <span className="btn-content">
                    Mehr erfahren
                  </span>
                </button>
              </motion.div>
            </div>

            {/* 3D Sphere */}
            <div className="hero-sphere-content">
              <motion.div
                initial={{ opacity: 0, scale: 1 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2, type: "spring" }}
                style={{ 
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  {show3D && (
                    <Suspense fallback={null}>
                      <ServiceVisualization 
                        serviceType={service.id} 
                        className="service-detail-visualization"
                      />
                    </Suspense>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="service-container" id="details">
          
          {/* Long Description */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ 
              maxWidth: '800px', 
              margin: '0 auto 5rem auto', 
              textAlign: 'center',
              fontSize: '1.25rem',
              lineHeight: '1.8',
              color: '#d1d5db',
              fontWeight: 300
            }}
          >
            {service.longDescription}
          </motion.div>

          {/* Bento Grid Layout */}
          <div className="service-bento-grid">
            
            {/* Features - Large Card */}
            <div className="bento-card features-card">
              <h2 className="card-title">Features</h2>
              <div className="features-list-compact">
                {service.features.map((feature, index) => (
                  <div key={index} className="feature-pill">
                    <span className="check-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits - Medium Card */}
            <div className="bento-card benefits-card">
              <h2 className="card-title">Deine Vorteile</h2>
              <ul className="benefits-list-compact">
                {service.benefits.map((benefit, index) => (
                  <li key={index}>
                    <span className="benefit-bullet" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* Tech Stack - Medium Card */}
            <div className="bento-card tech-card">
              <h2 className="card-title">Tech Stack</h2>
              <div className="tech-grid-compact">
                {service.technologies.map((techKey) => (
                  <div key={techKey} className="tech-item-compact" title={techKey}>
                    <img 
                      src={techIcons[techKey]} 
                      alt={techKey} 
                      className="tech-icon-compact"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Process - Wide Card */}
            <div className="bento-card process-card">
              <h2 className="card-title">Unser Prozess</h2>
              <div className="process-timeline-compact">
                <div className="step-connector-line" />
                {service.process.map((step, index) => (
                  <div key={index} className="process-step-compact">
                    <div className="step-number-compact">{index + 1}</div>
                    <div className="step-label">{step}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        <ContactSection />
      </div>
    </Layout>
  );
};

export default ServiceDetailPage;
